'use client'

import Image from 'next/image'
import { useState } from 'react'

import { stegaClean } from 'next-sanity'

import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { cn } from '@/app/utils/style'
import type { YoutubeEmbed as YoutubeEmbedType } from '@/sanity.types'
import { parseYoutubeUrl } from '@/utils/studio/embedPatterns'

interface YoutubeEmbedProps {
  value: YoutubeEmbedType
  className?: string
}

const EMBED_ORIGIN = 'https://www.youtube-nocookie.com/embed'
const THUMBNAIL_ORIGIN = 'https://i.ytimg.com/vi'

export function YoutubeEmbed({ value, className }: YoutubeEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const target = parseYoutubeUrl(stegaClean(value.url))
  const caption = stegaClean(value.caption)
  // Stored with the block (imported from YouTube or typed in Studio), so nothing is fetched at runtime
  const posterTitle = stegaClean(value.title) || null

  if (!target) return null

  const videoTitle = caption || posterTitle || 'YouTube video'
  const params = new URLSearchParams({ autoplay: '1', rel: '0' })
  if (target.startSeconds) params.set('start', String(target.startSeconds))

  return (
    <figure className={cn(ARTICLE_BLOCK_SPACING, className)}>
      <div className="relative aspect-video overflow-hidden rounded-sm bg-neutral-950 lg:rounded-md">
        {isPlaying ? (
          <iframe
            src={`${EMBED_ORIGIN}/${target.videoId}?${params.toString()}`}
            title={videoTitle}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsPlaying(true)}
            aria-label={`Play video: ${videoTitle}`}
            className="group absolute inset-0 flex h-full w-full cursor-pointer items-center justify-center text-white-default focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white-default"
          >
            {/* Default YouTube poster by video id; served by YouTube, so it skips the image optimizer */}
            <Image
              fill
              unoptimized
              src={`${THUMBNAIL_ORIGIN}/${target.videoId}/hqdefault.jpg`}
              alt=""
              sizes="(min-width: 800px) 800px, 100vw"
              className="object-cover"
            />
            {posterTitle && (
              <span className="pointer-events-none absolute inset-x-0 top-0 bg-linear-to-b from-black/70 to-transparent px-lg pt-lg pb-4xl text-left">
                <span className="size-body-md line-clamp-2 font-medium text-white-default">
                  {posterTitle}
                </span>
              </span>
            )}
            <span className="relative flex size-10xl items-center justify-center rounded-full bg-neutral-950/60 transition-colors group-hover:bg-neutral-950/80">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="ml-xs size-5xl fill-current">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {caption && <figcaption className="mt-md size-body-xs text-center">{caption}</figcaption>}
    </figure>
  )
}
